import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';  // ✅ Import Supabase
import LoginForm from './components/LoginForm';
import TwoFactorAuth from './components/TwoFactorAuth';
import SecurityTrustSignals from './components/SecurityTrustSignals';

const LoginPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('login'); // 'login' or '2fa'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // Check if user is already authenticated with Supabase
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/dashboard');
      }
    });
  }, [navigate]);

  const handleLoginSubmit = async (formData) => {
    setLoading(true);
    setError('');

    try {
      console.log('[Login] Attempting login with:', formData.email);

      // ✅ USE SUPABASE AUTH INSTEAD OF MOCK
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) {
        throw signInError;
      }

      console.log('[Login] ✅ Login successful:', data);

      // Store email for potential 2FA (if you implement it later)
      setUserEmail(formData.email);

      // Navigate to dashboard immediately (or go to 2FA if enabled)
      navigate('/dashboard');
      
    } catch (err) {
      console.error('[Login] ❌ Login failed:', err);
      setError(err.message || 'Invalid email or password. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerification = async (otpCode) => {
    setLoading(true);
    setError('');

    try {
      // For now, skip 2FA - you can implement this later with Supabase OTP
      console.log('[2FA] OTP verification:', otpCode);
      navigate('/dashboard');
      
    } catch (err) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('[2FA] Resending OTP to:', userEmail);
      // Implement OTP resend logic here if needed
      
    } catch (err) {
      setError('Failed to resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-lg security-shadow-card p-8">
          {currentStep === 'login' ? (
            <LoginForm
              onSubmit={handleLoginSubmit}
              loading={loading}
              error={error}
            />
          ) : (
            <TwoFactorAuth
              onVerify={handleOTPVerification}
              onResendOTP={handleResendOTP}
              loading={loading}
              error={error}
              email={userEmail}
            />
          )}
        </div>

        {/* Security Trust Signals */}
        <div className="mt-8">
          <SecurityTrustSignals />
        </div>

        {/* Demo Credentials Info */}
        <div className="mt-6 bg-muted/10 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Demo Credentials</h3>
          <div className="space-y-3 text-xs">
            <div>
              <p className="text-muted-foreground mb-1">Admin Account:</p>
              <p className="font-mono text-foreground">admin@securelock.com</p>
              <p className="font-mono text-foreground">SecureAdmin123!</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1">Test Account:</p>
              <p className="font-mono text-foreground">test@securelock.com</p>
              <p className="font-mono text-foreground">TestPass123!</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Note: Use actual Supabase credentials from your user_profiles table
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
